import { useState } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableValueArray,
} from "primereact/datatable";
import { ICandidate } from "../Candidate/Candidate.model";
import {
  DIFFICULTY_LEVELS,
  DIFFICULTY_LEVELS_LABEL,
} from "../../library/utilities/constants/constant";
import { ICandidateExam } from "../CandidateExam/CandidateExam.model";

function ExamAttempts(props: { selectedCandidates: ICandidate | null }) {
  const { selectedCandidates } = props;

  const [expandedRows, setExpandedRows] = useState<
    DataTableValueArray | DataTableExpandedRows | undefined
  >(undefined);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleString();
  };

  const columns = [
    {
      field: "id",
      header: "No of exam",
    },
    {
      field: "start_exam_date",
      header: "Start Exam Date",
      body: (row: ICandidateExam) => formatDate(row.start_exam_date),
    },
    {
      field: "submit_exam_date",
      header: "Submit Exam Date",
      body: (row: ICandidateExam) => formatDate(row.submit_exam_date),
    },
    {
      field: "percentage",
      header: "Percentage",
      body: (row: ICandidateExam) => `${row.percentage}%`,
    },
  ];

  const rowExpansionTemplate = (rowData: ICandidateExam) => {
    const interObj = rowData?.interview_question_object;

    if (!interObj) return <p>No interview question data found.</p>;

    const questions = interObj.interviewQuestions || [];
    const summary = {
      high: { correct: 0, total: 0 },
      medium: { correct: 0, total: 0 },
      low: { correct: 0, total: 0 },
    };
    const topicSummary: Record<
      string,
      { high: number; medium: number; low: number }
    > = {};
    let attempted = 0;
    let notAttempted = 0;

    questions.forEach((que: any) => {
      const topic = que.topic || "No Topic";
      if (!topicSummary[topic]) {
        topicSummary[topic] = { high: 0, medium: 0, low: 0 };
      }
      if (que.difficulty_level === DIFFICULTY_LEVELS.HIGH) {
        summary.high.total++;
        if (que.is_correct) summary.high.correct++;
        topicSummary[topic].high++;
      }
      if (que.difficulty_level === DIFFICULTY_LEVELS.MEDIUM) {
        summary.medium.total++;
        if (que.is_correct) summary.medium.correct++;
        topicSummary[topic].medium++;
      }
      if (que.difficulty_level === DIFFICULTY_LEVELS.LOW) {
        summary.low.total++;
        if (que.is_correct) summary.low.correct++;
        topicSummary[topic].low++;
      }
      if (que.attempted) {
        attempted++;
      } else {
        notAttempted++;
      }
    });

    const writingTest = interObj.writing_test;
    const questionsColumns = [
      {
        field: "topic",
        header: "Topic Name",
      },
      {
        field: "question_text",
        header: "Question",
      },
      {
        field: "difficulty_level",
        header: "Difficulty",
        body: (row: any) => DIFFICULTY_LEVELS_LABEL[row.difficulty_level],
      },
      {
        field: "selected_choice_value",
        header: "User answer",
      },
      {
        field: "correct_choice_value",
        header: "Correct answer",
      },
      {
        field: "is_correct",
        header: "Is Correct",
        body: (row: any) => (row.is_correct ? "Yes" : "No"),
      },
      {
        field: "marks_for_question",
        header: "Marks",
      },
      {
        field: "attempted",
        header: "Attempted",
        body: (row: any) => (row.attempted ? "Yes" : "No"),
      },
    ];

    return (
      <div className="p-3 border-round surface-50 mt-3">
        <h4 className="mb-3">Interview Questions</h4>
        {/** Dynamic columns for interview questions table **/}
        <DataTable
          value={questions}
          rowClassName={(row: any) =>
            row.attempted === false ? "bg-red-50" : ""
          }>
          {questionsColumns.map((col) => (
            <Column
              key={col.field}
              field={col.field}
              header={col.header}
              body={col.body}
            />
          ))}
        </DataTable>

        <div className="flex justify-content-between">
          <div className="mt-3">
            <h5>Summary</h5>

            <ul className="list-none p-0 m-0">
              {[
                { label: "Total Questions", value: questions.length },
                {
                  label: "Attempted",
                  value: attempted,
                  class: "text-green-700 font-bold",
                },
                {
                  label: "Not Attempted",
                  value: notAttempted,
                  class: "text-red-700 font-bold",
                },
              ].map((item) => (
                <li key={item.label} className="mb-2 flex align-items-center">
                  <span className="font-medium min-w-12rem">{item.label}:</span>
                  <span className={`ml-2 ${item.class || ""}`}>
                    {item.value}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Difficulty Summary */}
          <div className="mt-4">
            <ul className="list-none p-0 m-0">
              {[
                { label: "High Questions", data: summary.high },
                { label: "Medium Questions", data: summary.medium },
                { label: "Low Questions", data: summary.low },
              ].map((item) => (
                <li key={item.label} className="mb-2 flex align-items-center">
                  <span className="font-medium min-w-12rem">{item.label}:</span>

                  <span className="ml-2">
                    {item.data.correct}{" "}
                    <span className="text-green-600 font-bold">correct</span> /{" "}
                    <span className=" font-bold">{item.data.total} total</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-3">
          <h5>Writing Test Summary</h5>
          {Array.isArray(writingTest) &&
          writingTest.length > 0 &&
          writingTest[0] ? (
            <ul className="list-none p-0 m-0">
              <li className="mb-2 flex align-items-center">
                <span className="font-medium min-w-10rem">Speed:</span>
                <span className="ml-2">{writingTest[0].speed}</span>
              </li>
              <li className="mb-2 flex align-items-center">
                <span className="font-medium min-w-10rem">Accuracy:</span>
                <span className="ml-2">{writingTest[0].accuracy}</span>
              </li>
              <li className="mb-2 flex align-items-center">
                <span className="font-medium min-w-10rem">Start Time:</span>
                <span className="ml-2">{writingTest[0].startTime}</span>
              </li>
              <li className="mb-2 flex align-items-center">
                <span className="font-medium min-w-10rem">End Time:</span>
                <span className="ml-2">{writingTest[0].endTime}</span>
              </li>
            </ul>
          ) : (
            <div className="text-red-600 font-medium">
              Not conducted writing test
            </div>
          )}
        </div>

        <div className="mt-4">
          <h5>Topic-wise Question Count</h5>
          <ul className="list-none p-0 m-0">
            {Object.entries(topicSummary).map(([topic, counts]) => (
              <li key={topic} className="mb-1 flex align-items-center">
                <span className="font-medium min-w-10rem">{topic}:</span>
                <span className="ml-2 text-blue-700">High: {counts.high}</span>
                <span className="ml-3 text-orange-700">
                  Medium: {counts.medium}
                </span>
                <span className="ml-3 text-cyan-700">Low: {counts.low}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  return (
    <Card>
      <DataTable
        value={selectedCandidates?.exam_list || []}
        dataKey="id"
        paginator
        rows={10}
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        header={
          <div className="flex justify-content-between align-items-center">
            <h3>{selectedCandidates?.name} exam attempts list</h3>
          </div>
        }>
        <Column expander style={{ width: "3rem" }}></Column>

        {columns.map((col) => (
          <Column
            key={col.field}
            field={col.field}
            header={col.header}
            body={col?.body}
          />
        ))}
      </DataTable>
    </Card>
  );
}

export default ExamAttempts;
